import {
  FunctionComponent,
  createContext,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import rosetta from 'rosetta';

import { ROSSETA_LOCALE_KEY } from '../common/consts';
import ES from '../common/locales/es.json';
import EN from '../common/locales/en.json';

import DYNAMIC_ES from '../public/dynamic-i18n/es.json';
import DYNAMIC_EN from '../public/dynamic-i18n/en.json';

export const i18n = rosetta<Record<string, any>>();

const buildDynamicDict = (dynamicJson: Record<string, any>) => ({
  dynamic: { ...dynamicJson },
});

export const defaultLanguage = 'es';
const defaultDict = { ...ES, ...buildDynamicDict(DYNAMIC_ES) };
const dicts = {
  es: defaultDict,
  en: { ...EN, ...buildDynamicDict(DYNAMIC_EN) },
};
i18n.locale(defaultLanguage);
i18n.set(defaultLanguage, defaultDict);

export type I18nContextType = {
  activeLocale: string;
  t: (
    key: string | (string | number)[],
    params?: any[] | Record<string, any>,
    lang?: string
  ) => string;
  stagingT: (
    key: string | (string | number)[],
    params?: any[] | Record<string, any>,
    lang?: string
  ) => string;
  locale: (l: string) => void;
  table: (l: string) => void | Record<string, any>;
};

export const I18nContext = createContext<I18nContextType>(null);

const getActiveLocaleFromLocalStorage = (): string => {
  return typeof window === 'undefined'
    ? defaultLanguage
    : localStorage.getItem(ROSSETA_LOCALE_KEY) || defaultLanguage;
};

const I18n: FunctionComponent<{ children: React.ReactNode }> = ({
  children,
}) => {
  const activeLocale = getActiveLocaleFromLocalStorage();
  const activeDict = dicts[activeLocale] || defaultDict;
  const [, setTick] = useState(0);
  const firstRender = useRef(true);

  const setLocale = (lang: string, table: unknown): void => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', lang);
    }
    i18n.locale(lang);
    i18n.set(lang, table);
    // force rerender
    setTick((tick) => tick + 1);
  };

  // for initial SSR render
  if (firstRender.current === true) {
    firstRender.current = false;
    setLocale(activeLocale, activeDict);
  }

  useEffect(() => {
    const activeLocaleFromLocalStorage = getActiveLocaleFromLocalStorage();
    if (activeLocale !== activeLocaleFromLocalStorage) {
      const dict = dicts[activeLocaleFromLocalStorage];
      if (dict) {
        setLocale(activeLocaleFromLocalStorage, dict);
      }
    }
  }, [activeLocale]);

  const i18nWrapper = useMemo(
    () => ({
      activeLocale,
      t: (
        key: string | (string | number)[],
        params?: any[] | Record<string, any>,
        lang?: string
      ) => i18n.t(key, params, lang),
      stagingT: (
        key: string | (string | number)[],
        params?: any[] | Record<string, any>,
        lang?: string
      ) => {
        let result =
          process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production' &&
          i18n.t(key + 'Test', params, lang);
        return result || i18n.t(key, params, lang);
      },
      locale: (l: string) => {
        const dict = dicts[l];
        if (dict) {
          localStorage.setItem(ROSSETA_LOCALE_KEY, l);
          setLocale(l, dict);
        }
      },
      table: (l: string) => i18n.table(l),
    }),
    [activeLocale]
  );

  return (
    <I18nContext.Provider value={i18nWrapper}>{children}</I18nContext.Provider>
  );
};

export default I18n;
